import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function BrandCarousel() {
  const { data: brands = [], isLoading: loading } = useQuery({
    queryKey: ["brands-public"],
    queryFn: async () => (await api.get("/brands")).data,
  });

  return (
    <section id="brands" className="relative py-28 md:py-36 bg-[#0c1a14]" data-testid="brand-carousel">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[11px] uppercase tracking-[0.4em] text-gold">Clients</span>
              <span className="h-px w-16 bg-[#264738]" />
            </div>
            <h2 className="font-display text-ivory text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Brands we&apos;ve <span className="italic text-gold">collaborated</span> with.
            </h2>
          </div>
          <p className="text-ivory-muted text-sm md:text-base font-light max-w-sm">
            From beauty houses to fashion-first labels — partnered with names that demand the
            finest visual craft.
          </p>
        </motion.div>
      </div>

      <div className="marquee-fade">
        {loading ? (
          <div className="h-32 flex items-center justify-center text-ivory-muted text-sm">Loading…</div>
        ) : brands.length === 0 ? (
          <PlaceholderMarquee />
        ) : (
          <Marquee gradient={false} speed={40} pauseOnHover className="py-6">
            {brands.concat(brands).map((b, idx) => (
              <a
                key={`${b.id}-${idx}`}
                href={b.website || "#"}
                target={b.website ? "_blank" : undefined}
                rel="noreferrer"
                title={b.name}
                className="mx-10 md:mx-16 flex items-center justify-center h-20 md:h-28 grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-500"
                data-testid="brand-logo-item"
              >
                <img src={b.image_url} alt={b.name} className="max-h-full max-w-[160px] object-contain" />
              </a>
            ))}
          </Marquee>
        )}
      </div>
    </section>
  );
}

function PlaceholderMarquee() {
  const placeholders = [
    "Isaac Luxe", "Kérastase", "Forever New", "VLCC Luxe", "DLF Mall", "Worldmark Aerocity",
  ];
  return (
    <Marquee gradient={false} speed={40} pauseOnHover className="py-6">
      {placeholders.concat(placeholders).map((name, idx) => (
        <div
          key={`${name}-${idx}`}
          className="mx-8 md:mx-14 px-8 py-6 border border-[#264738] text-ivory-muted font-display text-xl md:text-2xl tracking-wider opacity-50"
        >
          {name}
        </div>
      ))}
    </Marquee>
  );
}
